import { playerToSlug } from "@/lib/playerSlug";
import { useState, useEffect } from "react";
import { Football, ArrowRight, ArrowLeft, Lightning, ArrowsLeftRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import { useIsAppNavVisible } from "@/hooks/useIsAppNavVisible";
import PublicNav from "@/components/PublicNav";

function PageNav() {
  return (
    <div className="border-b border-[#262626]">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline"><div className="w-7 h-7 bg-[#ccff00] flex items-center justify-center rounded-sm"><Football size={14} weight="bold" color="#000" /></div><span className="font-heading text-xl text-white">DYNASTY TRADE<span className="text-[#ccff00]">MAKER</span></span></Link>
        <div className="flex items-center gap-3">
          <Link to="/rankings" className="text-xs text-[#a3a3a3] hover:text-white no-underline hidden sm:block">Rankings</Link>
          <Link to="/guide" className="text-xs text-[#a3a3a3] hover:text-white no-underline hidden sm:block">Guide</Link>
          <Link to="/trade-calculator" className="text-xs text-[#ccff00] hover:text-white no-underline font-bold hidden sm:block">Calculator</Link>
        </div>
      </div>
    </div>
  );
}

function SFComparison() {
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [sfRes, qbRes] = await Promise.all([
          axios.get(`${API}/fantasycalc/values?numQbs=2&numTeams=12&ppr=1`),
          axios.get(`${API}/fantasycalc/values?numQbs=1&numTeams=12&ppr=1`),
        ]);
        const sfMap = {};
        for (const pv of sfRes.data) {
          if (pv.player?.position === "QB" && pv.value > 4000) {
            sfMap[pv.player.id] = { name: pv.player.name, team: pv.player.maybeTeam, sf: pv.value };
          }
        }
        for (const pv of qbRes.data) {
          if (sfMap[pv.player.id]) {
            sfMap[pv.player.id].oneqb = pv.value;
            sfMap[pv.player.id].diff = sfMap[pv.player.id].sf - pv.value;
          }
        }
        const sorted = Object.values(sfMap).sort((a, b) => b.diff - a.diff).slice(0, 5);
        setPlayers(sorted);
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (players.length === 0) return null;

  return (
    <div className="border border-[#262626] bg-[#171717] overflow-hidden my-6" data-testid="sf-comparison">
      <div className="px-4 py-2 bg-[#0a0a0a] border-b border-[#262626]">
        <p className="text-[9px] font-bold uppercase tracking-wider text-[#ccff00]">LIVE DATA — QB VALUE: SUPERFLEX VS 1QB</p>
      </div>
      <div className="grid grid-cols-4 gap-px px-4 py-2 border-b border-[#262626] text-[9px] font-bold uppercase tracking-wider text-[#555]">
        <span>PLAYER</span><span className="text-right">SUPERFLEX</span><span className="text-right">1QB</span><span className="text-right">SF PREMIUM</span>
      </div>
      {players.map((p, i) => (
        <Link key={i} to={`/players/${playerToSlug(p.name)}`} className="grid grid-cols-4 gap-px px-4 py-2.5 border-b border-[#1a1a1a] hover:bg-[#262626] no-underline transition-colors">
          <span className="text-xs text-white font-medium truncate">{p.name} <span className="text-[#666]">{p.team}</span></span>
          <span className="text-xs font-mono font-bold text-[#ccff00] text-right">{p.sf.toLocaleString()}</span>
          <span className="text-xs font-mono text-[#a3a3a3] text-right">{p.oneqb.toLocaleString()}</span>
          <span className="text-xs font-mono text-[#4ade80] text-right">+{p.diff.toLocaleString()}</span>
        </Link>
      ))}
    </div>
  );
}

export default function SuperflexGuide() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Dynasty Superflex Strategy Guide 2026 — Dynasty Trade Maker" description="How to draft and trade in Superflex dynasty leagues, with real current QB value comparisons showing exactly how much the format shifts quarterback value." path="/guide/superflex-strategy" image="https://dynastytrademaker.com/hero-images/guide-superflex.jpg" />
      <PublicNav />

      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/guide" className="inline-flex items-center gap-1 text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline mb-6 uppercase tracking-wider"><ArrowLeft size={10} /> GUIDE</Link>

        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">STRATEGY GUIDE</span>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white uppercase mb-4" data-testid="superflex-guide-title">DYNASTY SUPERFLEX <span className="text-[#ccff00]">STRATEGY</span></h1>

        {/* Hero Image */}
        <div className="mb-6 border border-[#262626] rounded-sm overflow-hidden">
          <img src="/hero-images/guide-superflex.jpg" alt="" className="w-full h-auto" loading="eager" />
        </div>
        <p className="text-base text-[#a3a3a3] leading-relaxed mb-8">Superflex is one roster spot — a flex that can hold a second quarterback — and it changes almost everything about how you should draft and trade. If you're playing Superflex with a 1QB strategy, you're leaving value on the table every single week.</p>

        <div className="space-y-10">
          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">WHY SUPERFLEX CHANGES EVERYTHING</h2>
            <p className="text-sm text-[#a3a3a3] leading-relaxed mb-3">In a 1QB league, only 12 quarterbacks start each week (one per team). In Superflex, up to 24 can start — every team wants two. That scarcity is the whole story: there simply aren't enough quality QBs to go around, so the ones who exist get paid a massive premium.</p>
            <SFComparison />
            <p className="text-[10px] text-[#555] italic">Live values from FantasyCalc. The SF Premium column shows exactly how much extra a QB is worth in Superflex vs. 1QB.</p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">HOW TO DRAFT SUPERFLEX</h2>
            <div className="space-y-4 text-sm text-[#a3a3a3] leading-relaxed">
              <p><strong className="text-white">Get two startable QBs before you worry about anything else.</strong> Not necessarily two elite ones — but leaving your second QB slot to the waiver wire in Superflex means starting a backup-caliber player every single week your bye/injury luck runs bad. That's a leaguewide-visible weakness your opponents will trade around.</p>
              <p><strong className="text-white">Rank QBs above where you're used to.</strong> If you're bringing 1QB instincts into a Superflex draft, you will get outbid on every quarterback and end up overpaying for RB/WR depth instead. Recalibrate before draft day, not during it.</p>
              <p><strong className="text-white">Don't punt on QB depth even once you have two starters.</strong> Injuries and bye weeks hit Superflex rosters harder — a third or fourth rostered QB isn't a luxury, it's a bye-week necessity.</p>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">HOW TO TRADE IN SUPERFLEX</h2>
            <div className="space-y-4 text-sm text-[#a3a3a3] leading-relaxed">
              <p><strong className="text-white">Sell your QB2/QB3 tier while it's high — buy it while it's dipping.</strong> Because so much of a QB's value in Superflex comes from scarcity rather than pure talent, these players swing on things that have nothing to do with their own performance. Watch the trend column, not just the raw number.</p>
              <p><strong className="text-white">A rebuilding team's QB1 is often their most movable asset.</strong> If a team is clearly selling, their startable quarterback should be your first call.</p>
              <p><strong className="text-white">Check both formats before every trade if you're unsure of your league's setup.</strong> A trade that's fair in 1QB can be lopsided in Superflex and vice versa — always confirm which chart you're using before you send an offer.</p>
            </div>
          </section>

          {/* CTA */}
          <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
            <h3 className="font-heading text-lg text-white uppercase mb-2">CHECK YOUR TRADE IN SUPERFLEX</h3>
            <p className="text-xs text-[#888] mb-4">Use the Trade Calculator set to Superflex to see the real value gap, or import your league for AI-powered trade packages.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/trade-calculator" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold"><ArrowsLeftRight size={12} /> OPEN TRADE CALCULATOR</Link>
              <a href="/" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">Import Your League <ArrowRight size={11} /></a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/guide/sleeper-setup" className="hover:text-[#a3a3a3] no-underline">Sleeper Setup</Link>
          <span>dynastytrademaker.com</span>
        </div>
      </div>
    </div>
  );
}
